import React from "react";
import {
  MDBBtn,
  MDBIcon,
  MDBTooltip,
  MDBInput,
  MDBRow,
  MDBCol,
} from "mdb-react-ui-kit";
import { doFetch } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";
import "./AdminUsersSection.css";

/**
 * AdminUsersSection — Gestión de usuarios de MUGIN.
 *
 * Hierarchy:
 *   MUGIN Admin   → Full admin (APP_ADMIN). Managed from SSO only.
 *   MUGIN Manager → Collaborator (APP_MANAGER). Assignable by admins.
 *   MUGIN          → Basic member. All new users get this group.
 *
 * Only MUGIN Admin users can see this section.
 * From here, admins can assign MUGIN and MUGIN Manager groups.
 * MUGIN Admin is NOT assignable from here (managed via SSO).
 *
 * Props: onLoading(boolean)
 */
class AdminUsersSection extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      users: [],
      groups: {},
      loaded: false,

      // New user form
      showNewForm: false,
      addMode: "existing", // "existing" | "new"
      newNombre: "",
      newApellido: "",
      newApodo: "",
      newPassword: "",
      newEmail: "",
      creating: false,

      // Search existing users
      searchQuery: "",
      searchResults: [],
      searching: false,
      searchDone: false,
    };
  }

  getFormResetState = () => ({
    addMode: "existing",
    newNombre: "",
    newApellido: "",
    newApodo: "",
    newPassword: "",
    newEmail: "",
    searchQuery: "",
    searchResults: [],
    searchDone: false,
  });

  toggleNewForm = () => {
    this.setState((prev) => {
      if (prev.showNewForm) {
        return { showNewForm: false, ...this.getFormResetState() };
      }
      return { showNewForm: true, ...this.getFormResetState() };
    });
  };

  componentDidMount() {
    this.loadUsers();
  }

  loadUsers() {
    if (this.props.onLoading) this.props.onLoading(true);

    doFetch("MUGIN", "getMUGINUsers")
      .then((res) => res.json())
      .then((json) => {
        if (json.STATE === "SUCCESS" && json.DATA) {
          this.setState({
            users: json.DATA.users || [],
            groups: json.DATA.groups || {},
            loaded: true,
          });
        } else {
          this.setState({ loaded: true });
          toast.show({ message: "Error al cargar usuarios", type: "danger" });
        }
      })
      .catch((err) => {
        console.error("Error loading MUGIN users:", err);
        this.setState({ loaded: true });
      })
      .finally(() => {
        if (this.props.onLoading) this.props.onLoading(false);
      });
  }

  removeUser = (user) => {
    if (this.props.onLoading) this.props.onLoading(true);

    doFetch("MUGIN", `removeMUGINUser?IDUsuario=${user.IDUsuario}`, {
      method: "PUT",
    })
      .then((res) => res.json())
      .then((json) => {
        if (json.STATE === "SUCCESS") {
          toast.show({
            message: json.MESSAGE || "Usuario retirado de MUGIN",
            type: "success",
          });
          this.loadUsers();
        } else {
          toast.show({
            message: json.MESSAGE || "Error al retirar usuario",
            type: "danger",
          });
          if (this.props.onLoading) this.props.onLoading(false);
        }
      })
      .catch(() => {
        toast.show({ message: "Error de conexión", type: "danger" });
        if (this.props.onLoading) this.props.onLoading(false);
      });
  };

  toggleManager = (user, assign) => {
    if (this.props.onLoading) this.props.onLoading(true);

    doFetch(
      "MUGIN",
      `setMUGINManager?IDUsuario=${user.IDUsuario}&assign=${assign}`,
      { method: "PUT" }
    )
      .then((res) => res.json())
      .then((json) => {
        if (json.STATE === "SUCCESS") {
          toast.show({
            message: assign
              ? "Manager asignado correctamente"
              : "Manager retirado correctamente",
            type: "success",
          });
          this.loadUsers();
        } else {
          toast.show({
            message: json.MESSAGE || "Error al actualizar rol",
            type: "danger",
          });
          if (this.props.onLoading) this.props.onLoading(false);
        }
      })
      .catch(() => {
        toast.show({ message: "Error de conexión", type: "danger" });
        if (this.props.onLoading) this.props.onLoading(false);
      });
  };

  searchUsers = () => {
    const { searchQuery } = this.state;
    if (!searchQuery || searchQuery.trim().length < 2) {
      toast.show({ message: "Escribe al menos 2 caracteres para buscar", type: "warning" });
      return;
    }

    this.setState({ searching: true, searchDone: false });

    doFetch("SSO", `searchUsers?q=${encodeURIComponent(searchQuery.trim())}`)
      .then((res) => res.json())
      .then((json) => {
        if (json.STATE === "SUCCESS" && json.DATA) {
          // Filter out users already in MUGIN groups
          const existingIds = new Set(this.state.users.map((u) => u.IDUsuario));
          const filtered = json.DATA.filter((u) => !existingIds.has(u.IDUsuario ?? u.iDUsuario));
          this.setState({ searchResults: filtered, searchDone: true });
        } else {
          this.setState({ searchResults: [], searchDone: true });
        }
      })
      .catch(() => {
        toast.show({ message: "Error al buscar usuarios", type: "danger" });
        this.setState({ searchDone: true });
      })
      .finally(() => this.setState({ searching: false }));
  };

  addExistingUser = (user) => {
    const userId = user.IDUsuario ?? user.iDUsuario;
    if (this.props.onLoading) this.props.onLoading(true);

    doFetch("MUGIN", `addExistingMUGINUser?IDUsuario=${userId}`, {
      method: "PUT",
    })
      .then((res) => res.json())
      .then((json) => {
        if (json.STATE === "SUCCESS") {
          toast.show({ message: json.MESSAGE || "Usuario añadido", type: "success" });
          this.setState({
            showNewForm: false,
            searchQuery: "",
            searchResults: [],
            searchDone: false,
          });
          this.loadUsers();
        } else {
          toast.show({ message: json.MESSAGE || "Error al añadir usuario", type: "danger" });
          if (this.props.onLoading) this.props.onLoading(false);
        }
      })
      .catch(() => {
        toast.show({ message: "Error de conexión", type: "danger" });
        if (this.props.onLoading) this.props.onLoading(false);
      });
  };

  createUser = () => {
    const { newNombre, newApellido, newApodo, newPassword, newEmail } = this.state;

    if (!newApodo || !newPassword) {
      toast.show({
        message: "El nombre de usuario y la contraseña son obligatorios",
        type: "warning",
      });
      return;
    }

    this.setState({ creating: true });
    if (this.props.onLoading) this.props.onLoading(true);

    doFetch("MUGIN", "createMUGINUser", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        nombre: newNombre,
        apellido: newApellido,
        apodo: newApodo,
        password: newPassword,
        email: newEmail,
        activo: 1,
      }),
    })
      .then((res) => res.json())
      .then((json) => {
        if (json.STATE === "SUCCESS") {
          toast.show({
            message: "Usuario creado correctamente",
            type: "success",
          });
          this.setState({
            showNewForm: false,
            ...this.getFormResetState(),
          });
          this.loadUsers();
        } else {
          toast.show({
            message: json.MESSAGE || "Error al crear usuario",
            type: "danger",
          });
          if (this.props.onLoading) this.props.onLoading(false);
        }
      })
      .catch(() => {
        toast.show({ message: "Error de conexión", type: "danger" });
        if (this.props.onLoading) this.props.onLoading(false);
      })
      .finally(() => {
        this.setState({ creating: false });
      });
  };

  hasPermission(user, permission) {
    return (user.permisos || []).includes(permission);
  }

  getGroupBadgeClass(user) {
    if (this.hasPermission(user, "APP_ADMIN")) return "badge-admin";
    if (this.hasPermission(user, "APP_MANAGE")) return "badge-manager";
    return "badge-member";
  }

  isAdmin(user) {
    return this.hasPermission(user, "APP_ADMIN");
  }

  isManager(user) {
    return this.hasPermission(user, "APP_MANAGE") && !this.hasPermission(user, "APP_ADMIN");
  }

  render() {
    const { users, loaded, showNewForm, creating } = this.state;

    if (!loaded) return null;

    return (
      <div className="admin-users-section">
        <h2 className="section-title">
          <MDBIcon fas icon="users-cog" className="me-2" />
          Gestión de usuarios MUGIN
        </h2>

        {/* Role hierarchy info */}
        <div className="hierarchy-card">
          <div className="hierarchy-title">Jerarquía de roles</div>
          <div className="hierarchy-level">
            <span className="level-icon">
              <MDBIcon fas icon="crown" style={{ color: "var(--color-acento)" }} />
            </span>
            <span className="group-badge badge-admin">MUGIN Admin</span>
            <span className="ms-2">
              — Administración completa. Se gestiona desde SSO.
            </span>
          </div>
          <div className="hierarchy-level">
            <span className="level-icon">
              <MDBIcon fas icon="user-tie" style={{ color: "var(--color-destacado)" }} />
            </span>
            <span className="group-badge badge-manager">MUGIN Manager</span>
            <span className="ms-2">
              — Colaborador. Puede modificar contenido de la web.
            </span>
          </div>
          <div className="hierarchy-level">
            <span className="level-icon">
              <MDBIcon fas icon="user" style={{ color: "var(--color-texto)" }} />
            </span>
            <span className="group-badge badge-member">MUGIN</span>
            <span className="ms-2">
              — Miembro básico. Todos los nuevos usuarios reciben este grupo.
            </span>
          </div>
        </div>

        {/* Actions bar */}
        <div className="d-flex justify-content-end mb-3">
          <MDBBtn
            className="mugin-btn-primary"
            size="sm"
            onClick={this.toggleNewForm}
          >
            <MDBIcon fas icon={showNewForm ? "times" : "user-plus"} className="me-1" />
            {showNewForm ? "Cancelar" : "Añadir usuario"}
          </MDBBtn>
        </div>

        {/* Add user form — dual mode */}
        {showNewForm && (
          <div className="new-user-card">
            {/* Mode tabs */}
            <div className="add-user-tabs">
              <button
                className={`add-user-tab ${this.state.addMode === "existing" ? "active" : ""}`}
                onClick={() => this.setState({ addMode: "existing" })}
              >
                <MDBIcon fas icon="search" className="me-1" />
                Añadir usuario existente
              </button>
              <button
                className={`add-user-tab ${this.state.addMode === "new" ? "active" : ""}`}
                onClick={() => this.setState({ addMode: "new" })}
              >
                <MDBIcon fas icon="user-plus" className="me-1" />
                Crear usuario nuevo
              </button>
            </div>

            {/* Mode: search existing */}
            {this.state.addMode === "existing" && (
              <div className="mt-3">
                <div className="d-flex gap-2 mb-3">
                  <MDBInput
                    label="Buscar por nombre, apellido o usuario"
                    value={this.state.searchQuery}
                    onChange={(e) => this.setState({ searchQuery: e.target.value })}
                    onKeyDown={(e) => e.key === "Enter" && this.searchUsers()}
                  />
                  <MDBBtn
                    className="mugin-btn-primary"
                    onClick={this.searchUsers}
                    disabled={this.state.searching}
                    style={{ minWidth: "100px" }}
                  >
                    <MDBIcon fas icon="search" className="me-1" />
                    {this.state.searching ? "..." : "Buscar"}
                  </MDBBtn>
                </div>

                {this.state.searchDone && this.state.searchResults.length === 0 && (
                  <div className="text-center py-3" style={{ opacity: 0.6 }}>
                    <MDBIcon fas icon="info-circle" className="me-1" />
                    No se encontraron usuarios disponibles
                  </div>
                )}

                {this.state.searchResults.length > 0 && (
                  <div className="search-results-list">
                    {this.state.searchResults.map((u) => (
                      <div key={u.IDUsuario ?? u.iDUsuario} className="search-result-item">
                        <div className="search-result-info">
                          {u.userImg || u.UserImg ? (
                            <img src={u.userImg || u.UserImg} alt="" className="user-avatar" />
                          ) : (
                            <div className="user-avatar-placeholder">
                              <MDBIcon fas icon="user" />
                            </div>
                          )}
                          <div>
                            <div className="fw-bold">
                              {u.nombre || u.Nombre} {u.apellido || u.Apellido}
                            </div>
                            <small style={{ opacity: 0.7 }}>
                              @{u.apodo || u.Apodo}
                              {(u.email || u.Email) ? ` · ${u.email || u.Email}` : ""}
                            </small>
                          </div>
                        </div>
                        <MDBBtn
                          className="mugin-btn-primary"
                          size="sm"
                          onClick={() => this.addExistingUser(u)}
                        >
                          <MDBIcon fas icon="plus" className="me-1" />
                          Añadir
                        </MDBBtn>
                      </div>
                    ))}
                  </div>
                )}

                <small style={{ color: "var(--color-texto)", opacity: 0.7 }}>
                  Busca un usuario ya registrado en el sistema para añadirlo al grupo <strong>MUGIN</strong>.
                </small>
              </div>
            )}

            {/* Mode: create new */}
            {this.state.addMode === "new" && (
              <div className="mt-3">
                <MDBRow>
                  <MDBCol md="4" className="mb-3">
                    <MDBInput
                      label="Nombre"
                      value={this.state.newNombre}
                      onChange={(e) => this.setState({ newNombre: e.target.value })}
                    />
                  </MDBCol>
                  <MDBCol md="4" className="mb-3">
                    <MDBInput
                      label="Apellido"
                      value={this.state.newApellido}
                      onChange={(e) => this.setState({ newApellido: e.target.value })}
                    />
                  </MDBCol>
                  <MDBCol md="4" className="mb-3">
                    <MDBInput
                      label="Email"
                      type="email"
                      value={this.state.newEmail}
                      onChange={(e) => this.setState({ newEmail: e.target.value })}
                    />
                  </MDBCol>
                </MDBRow>
                <MDBRow>
                  <MDBCol md="4" className="mb-3">
                    <MDBInput
                      label="Nombre de usuario *"
                      value={this.state.newApodo}
                      onChange={(e) => this.setState({ newApodo: e.target.value })}
                    />
                  </MDBCol>
                  <MDBCol md="4" className="mb-3">
                    <MDBInput
                      label="Contraseña *"
                      type="password"
                      value={this.state.newPassword}
                      onChange={(e) => this.setState({ newPassword: e.target.value })}
                    />
                  </MDBCol>
                  <MDBCol md="4" className="mb-3 d-flex align-items-center">
                    <MDBBtn
                      className="mugin-btn-primary"
                      onClick={this.createUser}
                      disabled={creating}
                    >
                      <MDBIcon fas icon="save" className="me-1" />
                      {creating ? "Creando..." : "Crear usuario"}
                    </MDBBtn>
                  </MDBCol>
                </MDBRow>
                <small style={{ color: "var(--color-texto)", opacity: 0.7 }}>
                  * Campos obligatorios. El usuario se asignará automáticamente al
                  grupo <strong>MUGIN</strong>.
                </small>
              </div>
            )}
          </div>
        )}

        {/* Users table */}
        {users.length === 0 ? (
          <div className="empty-state">
            <MDBIcon fas icon="users" size="2x" className="mb-2 d-block" />
            No hay usuarios en los grupos MUGIN
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table users-table">
              <thead>
                <tr>
                  <th style={{ width: "50px" }}></th>
                  <th>Nombre</th>
                  <th>Usuario</th>
                  <th>Email</th>
                  <th>Grupos</th>
                  <th style={{ width: "120px" }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.IDUsuario}>
                    <td>
                      {user.userImg ? (
                        <img
                          src={user.userImg}
                          alt=""
                          className="user-avatar"
                        />
                      ) : (
                        <div className="user-avatar-placeholder">
                          <MDBIcon fas icon="user" />
                        </div>
                      )}
                    </td>
                    <td>
                      {user.nombre} {user.apellido}
                    </td>
                    <td>{user.apodo}</td>
                    <td>{user.email || "—"}</td>
                    <td>
                      {(user.grupos || []).map((g, i) => (
                        <span
                          key={i}
                          className={`group-badge ${this.getGroupBadgeClass(user)}`}
                        >
                          {g}
                        </span>
                      ))}
                    </td>
                    <td>
                      {/* Admins are managed from SSO — show read-only label */}
                      {this.isAdmin(user) ? (
                        <small style={{ opacity: 0.5, fontStyle: "italic" }}>
                          Admin (SSO)
                        </small>
                      ) : (
                        <>
                          <MDBTooltip
                            tag="span"
                            title="Dar de baja en MUGIN"
                          >
                            <button
                              className="action-btn btn-remove"
                              onClick={() => {
                                if (window.confirm(`¿Dar de baja a ${user.apodo} en MUGIN? Se le retirarán todos los grupos de MUGIN.`)) {
                                  this.removeUser(user);
                                }
                              }}
                            >
                              <MDBIcon fas icon="user-slash" />
                            </button>
                          </MDBTooltip>

                          <MDBTooltip
                            tag="span"
                            title={
                              this.isManager(user)
                                ? "Retirar rol Manager"
                                : "Asignar rol Manager"
                            }
                          >
                            <button
                              className="action-btn btn-manager"
                              onClick={() => {
                                const action = this.isManager(user) ? "Retirar" : "Asignar";
                                if (window.confirm(`¿${action} rol Manager a ${user.apodo}?`)) {
                                  this.toggleManager(user, !this.isManager(user));
                                }
                              }}
                            >
                              <MDBIcon
                                fas
                                icon={
                                  this.isManager(user)
                                    ? "user-minus"
                                    : "user-tie"
                                }
                              />
                            </button>
                          </MDBTooltip>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }
}

export default AdminUsersSection;
